/**
 * NotificationDropdown Component
 * Popover dropdown for the bell icon showing recent notifications
 */
import React from 'react';
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  IconButton,
  Chip,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  AlertCircle,
  X as CloseIcon
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../../services/notificationService';
import { formatDistanceToNow } from 'date-fns';

interface NotificationDropdownProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  open,
  anchorEl,
  onClose
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch recent notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications', 'dropdown'],
    queryFn: () => notificationService.getNotifications({ limit: 10 }),
    enabled: open,
    staleTime: 1000 * 30 // 30 seconds
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) => notificationService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  });

  // Get icon based on notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TIMESHEET_APPROVED':
      case 'LEAVE_APPROVED':
        return <CheckCircle size={20} color={theme.palette.success.main} />;
      case 'TIMESHEET_REJECTED':
      case 'LEAVE_REJECTED':
        return <XCircle size={20} color={theme.palette.error.main} />;
      case 'APPROVAL_REQUIRED':
        return <Clock size={20} color={theme.palette.warning.main} />;
      case 'SYSTEM':
        return <AlertCircle size={20} color={theme.palette.info.main} />;
      default:
        return <Bell size={20} color={theme.palette.text.secondary} />;
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: any) => {
    // Mark as read
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.notification_id);
    }

    // Navigate to link if exists
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const notificationList = notifications?.notifications || [];
  const unreadCount = notificationList.filter((n: any) => !n.is_read).length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 600,
          overflow: 'hidden',
          mt: 1,
          boxShadow: theme.shadows[8]
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.05)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Bell size={20} />
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              color="error"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon size={18} />
        </IconButton>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Notifications List */}
      {!isLoading && (
        <>
          {notificationList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Bell size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 440, overflow: 'auto' }}>
              {notificationList.map((notification: any, index: number) => (
                <React.Fragment key={notification.notification_id}>
                  <ListItem
                    disablePadding
                    sx={{
                      bgcolor: !notification.is_read
                        ? alpha(theme.palette.primary.main, 0.05)
                        : 'transparent',
                      borderLeft: !notification.is_read
                        ? `3px solid ${theme.palette.primary.main}`
                        : '3px solid transparent'
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: !notification.is_read
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.action.hover, 0.05)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: !notification.is_read ? 600 : 400 }}>
                            {notification.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </Typography>
                          </Box>
                        }
                      />
                      {!notification.is_read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: theme.palette.primary.main,
                            ml: 1
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                  {index < notificationList.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Footer */}
          {notificationList.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              {unreadCount > 0 && (
                <Button
                  size="small"
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsReadMutation.isPending}
                >
                  Mark All as Read
                </Button>
              )}
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  navigate('/notifications');
                  onClose();
                }}
                sx={{ ml: 'auto' }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </>
      )}
    </Popover>
  );
};

export default NotificationDropdown;
